import styled, { css } from 'styled-components'

import { BORDER_RADIUS, MEDIA_QUERY } from '../../styles'
import { fieldWidth } from './constants'
import { FieldWidthType, LabelPosition } from './types'

export const disabledStyles = css`
  background-color: ${({ theme }) => theme.colors.input.disabled} !important;
  color: ${({ theme }) => theme.colors.font.disabled} !important;
  cursor: not-allowed !important;
`

export const baseInputStyles = css<{ width?: FieldWidthType; disabled?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.input.border};
  border-radius: ${BORDER_RADIUS};
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.background.white};
  padding: 0 10px;
`

export const responsiveStyles = css<{ responsive?: boolean }>`
  @media ${MEDIA_QUERY.TABLET} {
    margin: auto;
    ${({ responsive }) =>
      responsive &&
      css`
        width: 100%;
      `};
  }
  @media ${MEDIA_QUERY.MOBILE} {
    margin: auto;
    ${({ responsive }) =>
      responsive &&
      css`
        width: 100%;
      `};
  }
`

export const Container = styled.div<{ labelPosition?: LabelPosition }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ labelPosition }) => labelPosition === 'inline' && inlineLabelStyles};
  @media ${MEDIA_QUERY.MOBILE} {
    display: block;
    text-align: center;
  }
`

export const activeInputStyles = css`
  border-color: ${({ theme }) => theme.colors.input.active};
  outline: 1px solid ${({ theme }) => theme.colors.input.active};
`

export const InputContainer = styled('div').withConfig({
  shouldForwardProp: (prop) => !['disabled', 'responsive', 'width', 'isActive'].includes(prop)
})<{
  disabled?: boolean
  responsive?: boolean
  width?: FieldWidthType | undefined
  isActive?: boolean
}>`
  width: ${({ width }) => (width ? fieldWidth[width] : '100%')};
  background: ${({ theme, disabled }) => (disabled ? theme.colors.input.disabled : 'auto ')} !important;
  ${baseInputStyles};

  &:has(input:focus) {
    ${activeInputStyles}
  }

  &:has(input:disabled) {
    ${disabledStyles}
  }

  ${({ isActive }) => isActive && activeInputStyles}
  ${responsiveStyles};
`

export const Input = styled('input').withConfig({
  shouldForwardProp: (prop) => !['hasUnit'].includes(prop)
})<{ disabled?: boolean; hasUnit?: boolean }>`
  border: none;
  width: 100%;
  outline: none;
  box-shadow: none !important;
  height: 42px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.font.body};
  border-radius: ${({ hasUnit }) => (hasUnit ? `${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}` : BORDER_RADIUS)};
  ${({ disabled }) => disabled && disabledStyles}
`

export const Unit = styled.span<{ disabled?: boolean }>`
  padding-left: 10px;
  border-left: 1px solid ${({ theme }) => theme.colors.input.border};
  min-width: auto;
  font-size: ${({ theme }) => theme.typography.fontSize.minor};
  font-weight: 700;
  margin-left: 2px;
  height: 42px;
  line-height: 42px;
  vertical-align: middle;
  background: ${({ theme }) => theme.colors.background.white};
  ${({ disabled }) => disabled && disabledStyles};
  @supports selector(: nth-child(1 of x)) {
    margin-left: 0;
  }
`

export const Wrapper = styled.div`
  color: ${({ theme }) => theme.colors.font.body};
`

export const inlineLabelStyles = css`
  display: flex;
  align-items: center;
  gap: 20px;

  label {
    padding: 0;
  }

  @media ${MEDIA_QUERY.MOBILE} {
    width: 100%;
    display: inline-block;
  }
`
